<template>
    <v-dialog
        :value="value"
        @input="$emit('input', $event)"
        max-width="760"
        origin="top center"
        :aria-labelledby="idPrefix + '-dialog-title'"
        content-class="selection-dialog"
        scrollable
    >
        <v-card flat class="selection-dialog-card">
            <v-card-title class="selection-dialog-title">
                <span :id="idPrefix + '-dialog-title'">{{ title }}</span>
                <v-spacer />
                <v-btn @click="close" icon :aria-label="closeLabel">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <div class="selection-dialog-search">
                <v-text-field
                    v-model="search"
                    :label="searchLabel"
                    placeholder="Поиск"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    clearable
                    autofocus
                    :disabled="loading || !!error"
                />
            </div>
            <v-progress-linear v-if="loading" indeterminate />
            <v-card-text class="selection-dialog-list">
                <div v-if="error" class="error--text py-4">
                    {{ error }}
                    <v-btn @click="$emit('retry')" :loading="loading" small text color="primary">Повторить</v-btn>
                </div>
                <div v-if="!loading && !error && !filteredGroups.length" class="py-6 text-center grey--text">
                    {{ emptyLabel }}
                </div>
                <div v-for="group in filteredGroups" :key="group.id" class="selection-group">
                    <div class="selection-row">
                        <v-btn
                            @click="toggleExpanded(group.id)"
                            :aria-expanded="String(expanded.includes(group.id))"
                            :aria-controls="idPrefix + '-children-' + group.id"
                            :aria-label="(expanded.includes(group.id) ? 'Свернуть ' : 'Развернуть ') + group.name"
                            :disabled="!group.children.length"
                            icon
                            small
                            class="selection-expand"
                        >
                            <v-icon v-if="group.children.length" color="grey">{{ expanded.includes(group.id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                        </v-btn>
                        <v-checkbox
                            :input-value="isGroupSelected(group.id)"
                            :indeterminate="isGroupPartial(group.id)"
                            @change="toggleGroup(group.id)"
                            :label="group.name"
                            :disabled="loading || !!error"
                            hide-details
                            color="primary"
                            class="selection-checkbox"
                        />
                    </div>
                    <div
                        v-if="expanded.includes(group.id)"
                        :id="idPrefix + '-children-' + group.id"
                        class="selection-children"
                    >
                        <div v-for="child in group.children" :key="child.id" class="selection-row">
                            <v-checkbox
                                :input-value="isChildSelected(group.id, child.id)"
                                @change="toggleChild(group.id, child.id)"
                                :label="child.name"
                                :disabled="loading || !!error"
                                hide-details
                                color="primary"
                                class="selection-checkbox"
                            />
                        </div>
                    </div>
                </div>
            </v-card-text>
            <v-card-actions class="selection-dialog-actions">
                <v-btn @click="draft = []" :disabled="!draft.length" text color="primary">
                    Сбросить
                </v-btn>
                <v-spacer />
                <v-btn @click="close" text color="primary">Отменить</v-btn>
                <v-btn @click="save" :disabled="loading || !!error" color="primary" depressed>Сохранить</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'GroupedSelectionDialog',
    props: {
        value: Boolean,
        groups: { type: Array, default: () => [] },
        title: { type: String, required: true },
        searchLabel: { type: String, required: true },
        emptyLabel: { type: String, required: true },
        closeLabel: { type: String, required: true },
        idPrefix: { type: String, required: true },
        loading: Boolean,
        error: { type: String, default: '' },
        selection: { type: Array, default: () => [] },
    },
    data () {
        return { draft: [], search: '', expanded: [] }
    },
    computed: {
        filteredGroups () {
            const words = this.normalize(this.search).split(/\s+/).filter(Boolean)
            if (!words.length) return this.groups
            return this.groups.map(group => {
                const matchesGroup = words.every(word => this.normalize(group.name).includes(word))
                const children = matchesGroup ? group.children : group.children.filter(child => {
                    const text = this.normalize(group.name + ' ' + child.name)
                    return words.every(word => text.includes(word))
                })
                return { ...group, children, matchesGroup }
            }).filter(group => group.matchesGroup || group.children.length)
        },
    },
    watch: {
        value: {
            immediate: true,
            handler (open) {
                if (open) {
                    this.draft = [...this.selection]
                    this.search = ''
                    this.expanded = []
                }
            },
        },
        search (query) {
            this.expanded = this.normalize(query) ? this.filteredGroups.map(group => group.id) : []
        },
    },
    methods: {
        normalize (value) {
            return (value || '').toLocaleLowerCase('ru').replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()
        },
        isChildSelected (groupId, childId) {
            return this.draft.includes(groupId) || this.draft.includes(childId)
        },
        isGroupSelected (id) {
            const group = this.groups.find(group => group.id === id)
            return this.draft.includes(id) || (group.children.length > 0 && group.children.every(child => this.draft.includes(child.id)))
        },
        isGroupPartial (id) {
            const group = this.groups.find(group => group.id === id)
            return !this.isGroupSelected(id) && group.children.some(child => this.draft.includes(child.id))
        },
        toggleExpanded (id) {
            this.expanded = this.expanded.includes(id) ? this.expanded.filter(value => value !== id) : [...this.expanded, id]
        },
        toggleGroup (id) {
            const selected = this.isGroupSelected(id)
            const group = this.groups.find(group => group.id === id)
            const ids = new Set([id, ...group.children.map(child => child.id)])
            this.draft = this.draft.filter(value => !ids.has(value))
            if (!selected) this.draft.push(id)
        },
        toggleChild (groupId, childId) {
            const group = this.groups.find(group => group.id === groupId)
            if (this.draft.includes(groupId)) {
                this.draft = this.draft.filter(id => id !== groupId)
                this.draft = [...new Set([...this.draft, ...group.children.map(child => child.id)])]
            }
            this.draft = this.draft.includes(childId) ? this.draft.filter(id => id !== childId) : [...this.draft, childId]
            if (group.children.every(child => this.draft.includes(child.id))) {
                this.draft = this.draft.filter(id => !group.children.some(child => child.id === id))
                this.draft.push(groupId)
            }
        },
        close () {
            this.$emit('input', false)
        },
        save () {
            this.$emit('save', [...new Set(this.draft)])
            this.close()
        },
    },
}
</script>

<style scoped>
.selection-dialog-card {
    border-radius: inherit;
    overflow: hidden;
}
.selection-dialog-title {
    padding: 24px 28px 16px;
    font-size: 26px;
    line-height: 1.3;
    color: #202124;
}
.selection-dialog-search {
    flex: none;
    padding: 8px 28px 20px;
}
.selection-dialog-list {
    min-height: 0;
    padding: 0 28px 12px;
}
.selection-row {
    display: flex;
    align-items: center;
    min-height: 56px;
}
.selection-expand {
    flex: none;
    margin-right: 8px;
}
.selection-checkbox {
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 0;
}
.selection-checkbox ::v-deep .v-label {
    color: #202124;
    line-height: 1.5;
}
.selection-children {
    margin-left: 40px;
}
.selection-dialog-actions {
    padding: 20px 28px 24px;
    border-top: 1px solid #f0f0f0;
}
@media (max-width: 600px) {
    .selection-dialog-title {
        padding: 16px;
        font-size: 22px;
    }
    .selection-dialog-search, .selection-dialog-list {
        padding-left: 16px;
        padding-right: 16px;
    }
    .selection-dialog-actions {
        padding: 16px 12px;
    }
    .selection-dialog-actions ::v-deep .v-btn.v-btn {
        min-width: 0;
        font-size: 13px;
        letter-spacing: normal;
    }
}
</style>

<style>
.v-dialog.selection-dialog {
    align-self: flex-start;
    margin: 48px 24px;
    max-height: calc(100% - 96px);
    border-radius: 20px;
    overflow: hidden;
}

@media (max-width: 600px) {
    .v-dialog.selection-dialog {
        margin-top: 24px;
        margin-bottom: 24px;
        max-height: calc(100% - 48px);
    }
}
</style>
